import { QuestionCircleOutlined } from "@ant-design/icons";
import { Button, Drawer, Tooltip, Typography, theme } from "antd";
import React, { useState } from "react";
import { useConfig } from "../../contexts/ConfigContext";
import { ayudaDePantalla } from "../../config/ayuda/ayudasDePantalla";

const { Paragraph, Title } = Typography;

/**
 * El «?» de la cabecera de una pantalla, con las reglas de esa pantalla.
 *
 * ## Por qué un icono y no F1
 *
 * Esto corre en un navegador, donde F1 es la tecla de ayuda del propio
 * navegador: capturarla es poco fiable y se siente hostil. La convención en web
 * es `?`, pero en un TPV hay lectores de código de barras tecleando dentro de
 * los campos, y un atajo de una sola tecla se dispararía a media lectura. Un
 * icono en la cabecera no tiene ninguno de los dos problemas y además se ve,
 * que es la mitad del trabajo de una ayuda.
 *
 * ## Qué contiene
 *
 * Las reglas que no se adivinan mirando la pantalla. El contenido vive aparte,
 * en config/ayuda/ayudasDePantalla.js, para poder revisarlo sin abrir un
 * componente y para que se filtre solo según el país en el que se opera.
 *
 * @param {{ clave: string }} props  Cuál de las ayudas mostrar.
 */
const AyudaDePantalla = ({ clave }) => {
  const { token } = theme.useToken();
  const [abierta, setAbierta] = useState(false);
  // Sin el contexto de configuración --una prueba que pinta sólo un componente,
  // por ejemplo-- se enseña lo común a todos los países en vez de tumbar la
  // pantalla entera por un botón de ayuda.
  const { country } = useConfig() || {};
  const ayuda = ayudaDePantalla(clave, country?.code);

  // Sin contenido no se dibuja el icono: un «?» que abre una ventana vacía
  // enseña que la ayuda no sirve, y ya no se vuelve a pulsar.
  if (!ayuda || !ayuda.secciones.length) return null;

  return (
    <>
      <Tooltip title={`Cómo funciona: ${ayuda.titulo.toLowerCase()}`}>
        <Button
          type="text"
          shape="circle"
          icon={<QuestionCircleOutlined />}
          onClick={() => setAbierta(true)}
          aria-label={`Ayuda de ${ayuda.titulo}`}
        />
      </Tooltip>

      <Drawer
        title={`Cómo funciona: ${ayuda.titulo.toLowerCase()}`}
        placement="right"
        width={460}
        open={abierta}
        onClose={() => setAbierta(false)}
      >
        {ayuda.entradilla && (
          <Paragraph style={{ fontSize: 15, color: token.colorTextSecondary }}>
            {ayuda.entradilla}
          </Paragraph>
        )}

        {ayuda.secciones.map((seccion) => (
          <section key={seccion.titulo} style={{ marginTop: 22 }}>
            <Title level={5} style={{ marginBottom: 8 }}>
              {seccion.titulo}
            </Title>
            <ul style={{ paddingLeft: 18, margin: 0 }}>
              {seccion.puntos.map((punto) => (
                <li key={punto.texto} style={{ marginBottom: 10, lineHeight: 1.5 }}>
                  {punto.texto}
                </li>
              ))}
            </ul>
          </section>
        ))}
      </Drawer>
    </>
  );
};

/**
 * El título de una ventana modal con su «?» al lado.
 *
 * `Modal` de antd acepta un nodo como `title`, así que la ayuda se cuelga ahí y
 * queda en la misma cabecera. Y como el arrastre de modales ignora los botones
 * de la cabecera (ver hooks/modalesArrastrables.js), pulsar el «?» abre la
 * ayuda en vez de mover la ventana.
 *
 * @param {{ titulo: string, ayuda: string }} props
 */
export const TituloConAyuda = ({ titulo, ayuda }) => (
  <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
    {titulo}
    <AyudaDePantalla clave={ayuda} />
  </span>
);

export default AyudaDePantalla;
